import { ApiResult } from 'types';
import { fetchAuthSession } from 'aws-amplify/auth';
import i18n from 'i18next';
import { API_BASE_URL, API_PR_URL } from 'utils/constants';

let sessionExpiredHandler: (() => boolean) | null = null;

export const setSessionExpiredHandler = (handler: () => boolean) => {
  sessionExpiredHandler = handler;
};

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  let token: string | undefined;

  try {
    const session = await fetchAuthSession();
    token = session.tokens?.idToken?.toString();
  } catch {
    token = undefined;
  }

  if (!token && sessionExpiredHandler?.()) {
    throw new Error(i18n.t('toast.sessionExpired'));
  }

  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const handleUnauthorized = async () => {
  sessionExpiredHandler?.();
};

export const postRequest = async <T = ApiResult>(
  endpoint: string,
  payload: object
): Promise<T | undefined> => {
  const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (response.status === 401) {
    await handleUnauthorized();
    return;
  }

  return response.json();
};

export const putRequest = async <T = ApiResult>(
  endpoint: string,
  payload: object
): Promise<T | undefined> => {
  const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
    method: 'PUT',
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (response.status === 401) {
    await handleUnauthorized();
    return;
  }

  return response.json();
};

export const getRequest = async <T = ApiResult>(
  endpoint: string,
  baseUrl: string = API_BASE_URL
): Promise<T | undefined> => {
  const response = await fetch(`${baseUrl}/${endpoint}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
  });

  if (response.status === 401) {
    await handleUnauthorized();
    return;
  }

  return response.json();
};

export const putRequestWithPRBE = async (endpoint: string, payload?: object) => {
  const response = await fetch(`${API_PR_URL}/${endpoint}`, {
    method: 'PUT',
    headers: await getAuthHeaders(),
    ...(payload ? { body: JSON.stringify(payload) } : {}),
  });

  if (response.status === 401) {
    await handleUnauthorized();
    return;
  }

  return response.json();
};

export const postRequestWithPRBE = async (endpoint: string, payload: object) => {
  const response = await fetch(`${API_PR_URL}/${endpoint}`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (response.status === 401) {
    await handleUnauthorized();
    return;
  }

  return response.json();
};

export const getRequestWithPRBE = async (endpoint: string) => {
  const response = await fetch(`${API_PR_URL}/${endpoint}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
  });

  if (response.status === 401) {
    await handleUnauthorized();
    return;
  }

  return response.json();
};
